////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: 404 route — catch-all for `*`                                  //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { useEffect } from 'react';
import { Link } from 'react-router';

import { usePageTitle } from '@/hooks/usePageTitle';
import { GlassTile } from '@/components/content/GlassTile';
import { PageHero } from '@/components/content/PageHero';
import { Reveal } from '@/components/content/Reveal';

// A client-side router cannot set a real HTTP 404 status — that is Caddy's SPA
// fallback rewriting every unmatched path to index.html (gui/Caddyfile, out of
// scope for this route). "Proper 404 semantics" here means what the DOM/document
// CAN carry: a document.title that says so, and a `noindex` robots directive so
// this route is never indexed as if it were real content — both genuine syncs
// with an external system (the DOM head), hence the effect.
export function NotFound () {
  usePageTitle('Page not found');

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  return (
    <div role="alert">
      {/* MARK — <BackgroundLayer /> has no dedicated '404' PageKey (falls back
          to 'home', see components/chrome/BackgroundLayer.tsx) — flagged there
          already, not re-litigated here; <PageHero /> rides whatever
          background is currently resolved, same as every other page. */}
      <PageHero title="404 — Page not found" />
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <Reveal as="div">
          <GlassTile className="p-8 sm:p-12">
            <p className="text-lg text-muted">The page you're looking for doesn't exist.</p>
            <Link to="/" className="mt-6 inline-block underline">
              Return home
            </Link>
          </GlassTile>
        </Reveal>
      </div>
    </div>
  );
}
